import classnames from 'classnames';

import { getOrganizationGeneralInfo } from 'src/api/organizations';
import { useSelector } from 'react-redux';
import { selectAccount, selectAuthToken } from 'src/store/selectors';
import { useQuery } from '@tanstack/react-query';
import {
  CohortsIcon2,
  DatabaseIcon,
  OrganizationIcon,
  ResearcherAddIcon,
  StudyIcon,
} from '@medeloop/medeloop-components-library';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useMemo } from 'react';
import { UserGroup } from 'src/types/users';
import { TooltipButton } from '..';

export const OrganizationCard = ({ organization }: { organization: any }) => {
  const isOU = organization.organizationId.includes(':');
  const token = useSelector(selectAuthToken);
  const { t } = useTranslation();
  const account = useSelector(selectAccount);

  const navigate = useNavigate();

  const { data } = useQuery({
    queryKey: ['organization', organization.rootOrganizationId],
    queryFn: () => getOrganizationGeneralInfo({ id: organization.rootOrganizationId, token }),
    enabled: isOU,
  });
  const rootOrganization = data?.organization;

  const isAdmin = useMemo(() => {
    return (
      account.groups.includes(UserGroup.ADMIN) &&
      account.organizationAdminIds.some((id) => organization.organizationId.startsWith(id))
    );
  }, [account]);

  const handleClick = () => {
    navigate(`organizations/${organization.organizationId}`);
  };

  const handleInviteResearcher = (organizationId: string) => {
    navigate(`organizations/${organizationId}/invite-researcher`);
  };

  return (
    <div
      key={organization.organizationId}
      className={classnames(
        'flex flex-col w-[412px]  px-4 py-6 rounded gap-4 cursor-pointer',
        'bg-gray-900  border-sm border border-gray-800 hover:bg-gray-800 line-clamp-1',
      )}
      onClick={handleClick}
    >
      <div className="flex flex-row justify-between items-center">
        <div className="flex flex-col items-start gap-1">
          {isOU && rootOrganization && (
            <span className="text-gray-400 text-sm">{rootOrganization.displayName}</span>
          )}
          <span className="text-white font-bold">{organization.organizationName}</span>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <TooltipButton
              tooltipText={t('invite_researcher')}
              onClick={() => handleInviteResearcher(organization.organizationId)}
            >
              <ResearcherAddIcon className="h-5 w-5 text-cyan-300" />
            </TooltipButton>
          )}
        </div>
      </div>
      <div className="flex items-start justify-between text-white text-sm">
        <div className="flex flex-col grow-0 items-start gap-1">
          <div className="flex items-center gap-2 text-sm">
            <CohortsIcon2 className="h-5 w-5 text-yellow-300 " />
            <span>
              {organization.researchers}{' '}
              <span className="text-gray-400">{t('num_researchers')}</span>
            </span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <StudyIcon className="h-5 w-5 text-cyan-300	" />
            <span>
              {organization.studies} <span className="text-gray-400">{t('num_studies')}</span>
            </span>
          </div>
        </div>
        <div className="flex flex-col grow-0 items-start gap-1">
          {!isOU && (
            <div className="flex items-center gap-2 text-sm">
              <OrganizationIcon className="h-5 w-5 text-purple-300" />
              <span>
                {organization.organizationalUnits ?? 0}{' '}
                <span className="text-gray-400">{t('num_organizational_units')}</span>
              </span>
            </div>
          )}
          <div className="flex items-center gap-2 text-sm">
            <DatabaseIcon className="h-5 w-5 text-blue-400" />
            <span>
              {organization.dataSources}{' '}
              <span className="text-gray-400">{t('num_data_sources')}</span>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
